/**
 * FinancialResultCard - Collapsible card for completed financial analysis results
 *
 * Displays financial deep dive results in a chat-style feed:
 * - Collapsed: Compact header with ticker, winning side, confidence, decisive factor
 * - Expanded: Full FinancialAnalysisPanel with all details
 */

import { TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import { FinancialAnalysisPanel } from './FinancialAnalysisPanel';
import { ExportMenu } from './ExportMenu';
import { handleFinancialExport } from '@/utils/exportUtils';
import type { FinancialKillSheet, AnchoredSnapshot } from './FinancialAnalysisPanel';
import type { FinancialAgentProgress, MediatorCompilation } from './FinancialLogicTree';
import type { ExportFormat } from '@/types/researchModes';

interface FinancialResultCardProps {
  killSheet: FinancialKillSheet;
  anchoredSnapshot?: AnchoredSnapshot;
  query: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
  bullAgents?: FinancialAgentProgress[];
  bearAgents?: FinancialAgentProgress[];
  mediatorCompilation?: MediatorCompilation;
}

export function FinancialResultCard({
  killSheet,
  anchoredSnapshot,
  query,
  isCollapsed = false,
  onToggleCollapse,
  className,
  bullAgents,
  bearAgents,
  mediatorCompilation,
}: FinancialResultCardProps) {
  const isBullWin = killSheet.winning_side === 'bull';
  const confidencePercent = Math.round(killSheet.confidence * 100);
  const decisiveFactor = killSheet.kill_sheet_reason || killSheet.decisive_factor || '';

  const handleExport = async (format: ExportFormat) => {
    await handleFinancialExport(format, {
      killSheet,
      anchoredSnapshot,
      query,
      ticker: killSheet.ticker,
      bullAgents: bullAgents || [],
      bearAgents: bearAgents || [],
      mediatorCompilation,
    });
  };

  if (isCollapsed) {
    return (
      <div
        className={cn(
          'relative w-full bg-surface border overflow-hidden rounded-xl transition-all duration-300',
          isBullWin ? 'border-v-teal/30' : 'border-v-crimson/30',
          className
        )}
      >
        <div className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              {/* Bull/Bear icon */}
              <Tooltip content={isBullWin ? 'Bullish outlook' : 'Bearish outlook'} position="top">
                <div
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                    isBullWin ? 'bg-v-teal/20' : 'bg-v-crimson/20'
                  )}
                >
                  {isBullWin ? (
                    <TrendingUp className="w-4 h-4 text-v-teal" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-v-crimson" />
                  )}
                </div>
              </Tooltip>

              {/* Ticker + Verdict */}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-bold text-sm text-text-primary">
                    {killSheet.ticker}
                  </span>
                  <span
                    className={cn(
                      'text-xs font-bold uppercase',
                      isBullWin ? 'text-v-teal' : 'text-v-crimson'
                    )}
                  >
                    {isBullWin ? 'BULL CASE WINS' : 'BEAR CASE WINS'}
                  </span>
                  <Tooltip content="How decisively evidence favors this direction" position="top">
                    <span
                      className={cn(
                        'px-1.5 py-0.5 rounded text-xs font-mono cursor-help',
                        isBullWin
                          ? 'bg-v-teal/20 text-v-teal'
                          : 'bg-v-crimson/20 text-v-crimson'
                      )}
                    >
                      {confidencePercent}%
                    </span>
                  </Tooltip>
                </div>
                {decisiveFactor && (
                  <p className="text-xs text-text-muted mt-0.5 truncate">
                    {decisiveFactor}
                  </p>
                )}
              </div>
            </div>

            {/* Expand button */}
            {onToggleCollapse && (
              <Tooltip content="Expand full analysis" position="left">
                <button
                  onClick={onToggleCollapse}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors flex-shrink-0"
                >
                  <ChevronDown className="w-4 h-4" />
                  <span className="hidden sm:inline">Details</span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Expanded state - full FinancialAnalysisPanel
  return (
    <div
      className={cn(
        'relative w-full bg-surface border overflow-hidden rounded-xl transition-all duration-300',
        isBullWin ? 'border-v-teal/30' : 'border-v-crimson/30',
        className
      )}
    >
      {/* Header with collapse toggle */}
      <div className="px-4 py-3 border-b border-border-subtle bg-bar-bg/50">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center',
                isBullWin ? 'bg-v-teal/20' : 'bg-v-crimson/20'
              )}
            >
              {isBullWin ? (
                <TrendingUp className="w-4 h-4 text-v-teal" />
              ) : (
                <TrendingDown className="w-4 h-4 text-v-crimson" />
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono font-bold text-sm text-text-primary">
                {killSheet.ticker}
              </span>
              <Tooltip content="In-depth bull vs bear investment analysis" position="top">
                <span className="font-mono text-xs text-text-muted uppercase tracking-wider cursor-help">
                  Financial Deep Dive
                </span>
              </Tooltip>
              <span
                className={cn(
                  'px-1.5 py-0.5 rounded text-xs font-mono',
                  isBullWin
                    ? 'bg-v-teal/20 text-v-teal'
                    : 'bg-v-crimson/20 text-v-crimson'
                )}
              >
                {confidencePercent}% confidence
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <ExportMenu
              onExport={handleExport}
              reportTitle={`${killSheet.ticker} Financial Analysis`}
            />
            {onToggleCollapse && (
              <Tooltip content="Collapse to summary" position="left">
                <button
                  onClick={onToggleCollapse}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
                >
                  <ChevronUp className="w-4 h-4" />
                  <span className="hidden sm:inline">Collapse</span>
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {/* Full analysis panel */}
      <div className="p-4">
        <FinancialAnalysisPanel
          killSheet={killSheet}
          anchoredSnapshot={anchoredSnapshot}
        />
      </div>
    </div>
  );
}
